import {useEffect, useState} from 'react';

import {useI18n} from '@udemy/i18n';
import {HeroBannerSlideProps} from '@udemy/shared-one-udemy-components';
import {udApi} from '@udemy/ud-api';
import {udLink} from '@udemy/ud-data';

export const NOTICE_TYPES = {
    BANNER_CAROUSEL_SLIDE: 'banner_carousel_slide',
    LOHP_HERO_BANNER: 'lohp_hero_banner',
    FALLBACK_BANNER: 'fallback_banner',
};

export type CTATargetType = 'self' | 'new_tab';

export interface BannerData {
    id?: number;
    type?: string;
    personalized_notice_set_id?: number;
    personalized_notice_set_name?: string;
    title: string;
    title_responsive: string;
    subtitle: string;
    subtitle_responsive: string;
    url?: string;
    cta_1_target?: CTATargetType;
    cta_1_text?: string;
    cta_1_url?: string;
    cta_2_target?: CTATargetType;
    cta_2_text?: string;
    cta_2_url?: string;
    image: string;
    image_responsive: string;
    background_color?: string;
    target_group_id?: number | null;
    layout: HeroBannerSlideProps['slideLayout'];
}

/**
 * Fetches hero banners by firing the variant and control requests in parallel,
 * then picking which result to render:
 *   1. If the variant (LOHP_HERO_BANNER / 'lohp_hero_banner') request succeeds,
 *      render the variant.
 *   2. Otherwise, if the control (BANNER_CAROUSEL_SLIDE) request succeeds,
 *      render the control.
 *   3. If both requests fail, fall back to a hardcoded banner.
 *
 * Firing in parallel trades extra bandwidth (one always-wasted request) for
 * a single round-trip of latency for control-bucketed users.
 */
export const useFetchHeroBanners = () => {
    const {gettext} = useI18n();
    const [noticeType, setNoticeType] = useState<string>(NOTICE_TYPES.LOHP_HERO_BANNER);
    const [isHeroBannerLoading, setIsHeroBannerLoading] = useState(true);
    const [heroBanners, setHeroBanners] = useState<{data: BannerData}[]>([]);

    useEffect(() => {
        const getNoticeType = (banners: {data?: {id?: number}}[], requestedType: string) =>
            // Fallback Banner is hardcoded and its id is always 0
            banners[0]?.data?.id === 0 ? NOTICE_TYPES.FALLBACK_BANNER : requestedType;

        const fetchByType = async (type: string) => {
            const searchParams = new URLSearchParams(window.location.search);
            const params = Object.fromEntries(searchParams.entries());
            const response = await udApi.get(`/notices/me/`, {
                params: {type, ...params},
            });
            const results = response.data?.results;
            // Treat a missing/empty result set as a failed fetch so the fallback
            // chain kicks in. Without this, an empty variant response would
            // "win" and we'd render an empty banner.
            if (!Array.isArray(results) || results.length === 0) {
                throw new Error(`/notices/me/?type=${type} returned no banners`);
            }
            return results as {data: BannerData}[];
        };

        const applyResult = (banners: {data: BannerData}[], requestedType: string) => {
            setNoticeType(getNoticeType(banners, requestedType));
            setHeroBanners(banners);
            setIsHeroBannerLoading(false);
        };

        const applyHardcodedFallback = () => {
            setNoticeType(NOTICE_TYPES.FALLBACK_BANNER);
            setHeroBanners([
                {
                    data: {
                        id: 0,
                        title: gettext('Learning that gets you'),
                        subtitle: gettext(
                            'Skills for your present (and your future). Get started with us.',
                        ),
                        title_responsive: gettext('Learning that gets you'),
                        subtitle_responsive: gettext(
                            'Skills for your present (and your future). Get started with us.',
                        ),
                        image: udLink.toStorageStaticAsset(
                            'browse_components/billboard/fallback_banner_image_udlite.jpg',
                        ),
                        image_responsive: udLink.toStorageStaticAsset(
                            'browse_components/billboard/fallback_banner_image_responsive_udlite.jpg',
                        ),
                        layout: 'wide',
                    },
                },
            ]);
            setIsHeroBannerLoading(false);
        };

        const fetchHeroBanners = async () => {
            setIsHeroBannerLoading(true);

            const [variantResult, controlResult] = await Promise.allSettled([
                fetchByType(NOTICE_TYPES.LOHP_HERO_BANNER),
                fetchByType(NOTICE_TYPES.BANNER_CAROUSEL_SLIDE),
            ]);

            if (variantResult.status === 'fulfilled') {
                applyResult(variantResult.value, NOTICE_TYPES.LOHP_HERO_BANNER);
                return;
            }

            if (controlResult.status === 'fulfilled') {
                applyResult(controlResult.value, NOTICE_TYPES.BANNER_CAROUSEL_SLIDE);
                return;
            }

            applyHardcodedFallback();
        };

        fetchHeroBanners();
    }, [gettext]);

    return {
        noticeType,
        heroBanners,
        isHeroBannerLoading,
    };
};
